import React, { useCallback, useState } from 'react';
// import { useDropzone } from 'react-dropzone'; // Removed as we use native drag-n-drop
// Actually user didn't ask to install react-dropzone, so I'll use native drag events or standard input to be safe/minimal, 
// OR I check package.json again. 
// package.json had dnd-kit but that's for sorting. 
// I'll stick to a simple click/drag implementation without extra deps if possible, or perform a manual implementation.
// Let's implement a manual drag-drop to avoid installing more deps if I can help it, or just use input type=file heavily styled.
// Actually, I'll use a file input hidden and a label.

import { Upload, FileVideo, AlertCircle } from 'lucide-react';
import { useVideoUpload } from '@/hooks/useVideoUpload';
import { UploadProgress } from './UploadProgress';
import { Button } from '@/components/ui/button'; // Assuming shadcn-like components exist as per previous listing

interface VideoUploaderProps {
    projectId?: string;
    onUploadComplete?: () => void;
}

export function VideoUploader({ projectId: existingProjectId, onUploadComplete }: VideoUploaderProps = {}) {
    const { upload, progress, status, error, projectId } = useVideoUpload(existingProjectId);
    const [dragActive, setDragActive] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);

    // Call success callback when completed
    React.useEffect(() => {
        if (status === 'completed' && onUploadComplete) {
            onUploadComplete();
        }
    }, [status, onUploadComplete]);

    const handleFile = useCallback((file: File) => {
        if (file && file.type.startsWith('video/')) {
            setSelectedFile(file);
            upload(file);
        } else {
            alert('Please upload a valid video file');
        }
    }, [upload]);

    const handleDrag = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFile(e.dataTransfer.files[0]);
        }
    }, [handleFile]);

    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        e.preventDefault();
        if (e.target.files && e.target.files[0]) {
            handleFile(e.target.files[0]);
        }
    }, [handleFile]);

    return (
        <div className="w-full max-w-xl mx-auto">
            {status === 'idle' || status === 'error' ? (
                <div
                    className={`relative border-2 border-dashed rounded-xl p-8 transition-colors text-center ${dragActive
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50 hover:bg-muted/50'
                        }`}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                >
                    <input
                        type="file"
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                        accept="video/*"
                        onChange={handleChange}
                    />

                    <div className="flex flex-col items-center justify-center space-y-4">
                        <div className="p-4 bg-muted rounded-full">
                            <Upload className="w-8 h-8 text-muted-foreground" />
                        </div>

                        <div className="space-y-1">
                            <p className="text-lg font-medium">
                                Tap to upload or drag video
                            </p>
                            <p className="text-sm text-muted-foreground">
                                MP4, MOV, or WebM (max 500MB)
                            </p>
                        </div>

                        {error && (
                            <div className="flex items-center text-red-500 text-sm mt-4 bg-red-50 p-2 rounded">
                                <AlertCircle className="w-4 h-4 mr-2" />
                                <span>{error}</span>
                            </div>
                        )}

                        <Button variant="outline" className="mt-4 pointer-events-none">
                            Select Video
                        </Button>
                    </div>
                </div>
            ) : (
                <UploadProgress
                    progress={progress}
                    status={status}
                    fileName={selectedFile?.name}
                />
            )}
        </div>
    );
}
